import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Refresh as RefreshIcon, 
  History as HistoryIcon,
  PlayArrow as PlayArrowIcon,
  Book as BookIcon,
  Quiz as QuizIcon,
  Style as StyleIcon,
  CalendarToday as CalendarIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon
} from '@mui/icons-material';
import { Button, CircularProgress, Tooltip } from '@mui/material';
import apiService from '../services/api';

const RelearnContentCard = ({ 
  subject, 
  chapter, 
  contentType, 
  onRelearn, 
  onRegenerate,
  onClose 
}) => {
  const [existingContent, setExistingContent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    fetchExistingContent();
  }, [subject, chapter, contentType]);

  const fetchExistingContent = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await apiService.getExistingContent(
        contentType, 
        subject, 
        chapter, 
        1, 
        10
      );
      
      setExistingContent(response.items || []);
    } catch (err) {
      console.error('Error fetching existing content:', err);
      setError('Failed to load existing content');
      setExistingContent([]);
    } finally {
      setLoading(false);
    }
  };

  const handleRelearn = async (contentId, content) => {
    try {
      const response = await apiService.relearnContent(contentType, contentId);
      onRelearn(contentType, response, content);
    } catch (err) {
      console.error('Error relearning content:', err);
      setError('Failed to load content for relearning');
    }
  };

  const handleRegenerate = async () => {
    try {
      setGenerating(true);
      setError(null);
      
      let response;
      if (contentType === 'study') {
        response = await apiService.generateStudyContent(subject, chapter);
      } else if (contentType === 'flashcard') {
        response = await apiService.generateFlashcards(subject, chapter);
      } else if (contentType === 'quiz') {
        response = await apiService.generateQuiz(subject, chapter);
      }
      
      onRegenerate(contentType, response);
      
      // Refresh existing content list
      await fetchExistingContent();
    } catch (err) {
      console.error('Error generating new content:', err);
      setError('Failed to generate new content');
    } finally {
      setGenerating(false);
    }
  };

  const getContentIcon = (type) => {
    switch (type) {
      case 'study':
        return <BookIcon className="text-blue-600" />;
      case 'quiz':
        return <QuizIcon className="text-green-600" />;
      case 'flashcard':
        return <StyleIcon className="text-purple-600" />;
      default:
        return <HistoryIcon className="text-gray-600" />;
    }
  };

  const getContentTypeName = (type) => {
    switch (type) {
      case 'study':
        return 'Study Materials';
      case 'quiz':
        return 'Quiz Questions';
      case 'flashcard':
        return 'Flashcards';
      default:
        return 'Content';
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getContentPreview = (content) => {
    if (contentType === 'study') {
      // Extract the summary or detailed_content from the JSON object
      const studyContent = content.content?.summary || content.content?.detailed_content || content.content?.title || '';
      // Ensure it's a string before calling substring
      const contentString = typeof studyContent === 'string' ? studyContent : JSON.stringify(studyContent);
      return contentString.substring(0, 100) + (contentString.length > 100 ? '...' : '');
    } else if (contentType === 'quiz') {
      const questions = content.content?.questions || [];
      return `${questions.length} questions available`;
    } else if (contentType === 'flashcard') {
      const flashcards = content.content?.flashcards || [];
      return `${flashcards.length} flashcards available`;
    }
    return 'Content available';
  };

  if (loading) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 p-6"
      >
        <div className="flex items-center justify-center">
          <CircularProgress size={24} className="text-blue-600" />
          <span className="ml-3 text-gray-600 dark:text-gray-400">Loading existing content...</span>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden"
    >
      {/* Header */}
      <div className="bg-gradient-to-r from-orange-50 to-red-50 dark:from-orange-900/20 dark:to-red-900/20 p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {getContentIcon(contentType)}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                {existingContent.length > 0 ? 'Relearn' : 'Generate'} {getContentTypeName(contentType)}
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {existingContent.length > 0 
                  ? `${existingContent.length} previous ${contentType === 'flashcard' ? 'flashcard sets' : contentType === 'quiz' ? 'quizzes' : 'study materials'} available`
                  : `No existing ${contentType} content found`
                }
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {existingContent.length > 0 && (
              <button
                onClick={() => setIsExpanded(!isExpanded)}
                className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              >
                {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </button>
            )}
            <button
              onClick={onClose}
              className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            >
              ×
            </button>
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="p-4 bg-red-50 dark:bg-red-900/20 border-l-4 border-red-400">
          <p className="text-red-700 dark:text-red-300 text-sm">{error}</p>
        </div>
      )}

      {/* Content List */}
      {existingContent.length > 0 && (
        <motion.div
          initial={false}
          animate={{ height: isExpanded ? 'auto' : 0, opacity: isExpanded ? 1 : 0 }}
          transition={{ duration: 0.3 }}
          className="overflow-hidden"
        >
          <div className="p-4 space-y-3 max-h-64 overflow-y-auto">
            {existingContent.map((content, index) => (
              <motion.div
                key={content.id || index}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
                className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3 hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors cursor-pointer"
                onClick={() => handleRelearn(content.id, content)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <PlayArrowIcon className="text-orange-600 text-sm" />
                      <span className="text-sm font-medium text-gray-900 dark:text-white">
                        {getContentTypeName(contentType)} #{index + 1}
                      </span>
                    </div>
                    <p className="text-xs text-gray-600 dark:text-gray-400 mb-2">
                      {getContentPreview(content)}
                    </p>
                    <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                      <CalendarIcon className="text-xs" />
                      <span>{formatDate(content.created_at)}</span>
                    </div>
                  </div>
                  <div className="text-right">
                    <span className="text-xs bg-orange-100 dark:bg-orange-900 text-orange-800 dark:text-orange-200 px-2 py-1 rounded-full">
                      Click to Relearn
                    </span>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      )}

      {/* Action Buttons */}
      <div className="p-4 bg-gray-50 dark:bg-gray-700 border-t border-gray-200 dark:border-gray-600">
        <div className="flex gap-3">
          <Button
            onClick={handleRegenerate}
            disabled={generating}
            variant="contained"
            startIcon={generating ? <CircularProgress size={16} /> : <RefreshIcon />}
            className="flex-1 bg-orange-600 hover:bg-orange-700 disabled:bg-orange-400"
          >
            {generating ? 'Generating...' : existingContent.length > 0 ? 'Generate New' : 'Generate'}
          </Button>
          {existingContent.length > 0 && (
            <Button
              onClick={() => setIsExpanded(!isExpanded)}
              variant="outlined"
              className="text-gray-600 dark:text-gray-400"
            >
              {isExpanded ? 'Show Less' : 'Show More'}
            </Button>
          )}
        </div>
      </div>
    </motion.div>
  );
};

export default RelearnContentCard; 